import {
  buildPayslipInfoTableRows,
  categorizeDeductions,
  collectActualEarnings,
  flattenDeductionBuckets,
  hasPayslipFieldValue,
} from "./payslipPresentation";
import "./PayslipDocument.css";

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function otHourlyCharge(extra) {
  const given = Number(extra?.hourly_rate);
  if (Number.isFinite(given) && given > 0) return given;
  const hours = Number(extra?.hours);
  const amount = Number(extra?.amount);
  if (Number.isFinite(hours) && hours > 0 && Number.isFinite(amount)) return amount / hours;
  return null;
}

function periodTitle(payPeriod) {
  if (!payPeriod) return "—";
  if (payPeriod.label) return String(payPeriod.label).toUpperCase();
  const end = payPeriod.end_date;
  if (end) {
    try {
      const d = new Date(end);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }).toUpperCase();
      }
    } catch {
      /* ignore */
    }
  }
  return "PAY PERIOD";
}

export default function PayslipDocument({ data }) {
  if (!data) return null;

  const org = data.organisation || {};
  const emp = data.employee || {};
  const payPeriod = data.pay_period || {};
  const att = data.attendance_detail || data.attendance_summary || {};
  const sections = data.sections || {};

  const earnings = collectActualEarnings(data.earnings || sections.earnings || [], sections);
  const deductions = flattenDeductionBuckets(
    categorizeDeductions(data.deductions || data.deduction_lines || sections.deductions || [])
  );
  const grossTotal = Number(data.gross_salary) || 0;
  const totalDeductions = Number(data.total_deductions) || 0;
  const netSalary = Number(data.net_salary) || 0;
  const fuelLine = (sections.reimbursements || []).find(
    (row) => String(row?.code || "").toUpperCase() === "FUEL_REIMBURSEMENT"
  );
  const fuelAmount = Number(data.fuel_reimbursement ?? fuelLine?.amount);
  const showFuel = Number.isFinite(fuelAmount) && fuelAmount > 0;

  const companyName = (org.legal_name || org.name || "Company").toUpperCase();
  const empName =
    emp.full_name ||
    data.employee_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    "Employee";

  const infoRows = buildPayslipInfoTableRows({ emp, org, att, empName });

  const showDeductionColumn = !(data.deductions_suppressed && deductions.length === 0);
  const rowCount = Math.max(earnings.length, showDeductionColumn ? deductions.length : 0, 1);
  const payRows = Array.from({ length: rowCount }, (_, i) => ({
    earn: earnings[i] || null,
    ded: deductions[i] || null,
  }));

  return (
    <article className="ps ps--standard" aria-label="Payslip">
      <header className="ps-std-header">
        <h1 className="ps-std-company">{companyName}</h1>
        {org.address_line && <p className="ps-std-address">{org.address_line}</p>}
        <h2 className="ps-std-title">PAYSLIP - {periodTitle(payPeriod)}</h2>
      </header>

      {infoRows.length > 0 && (
        <table className="ps-std-table ps-std-info">
          <tbody>
            {infoRows.map((row, i) => (
              <tr key={i}>
                {row.left ? (
                  <>
                    <th scope="row">{row.left[0]}</th>
                    <td>{row.left[1]}</td>
                  </>
                ) : (
                  <>
                    <th scope="row"></th>
                    <td></td>
                  </>
                )}
                {row.right ? (
                  <>
                    <th scope="row">{row.right[0]}</th>
                    <td>{row.right[1]}</td>
                  </>
                ) : (
                  <>
                    <th scope="row"></th>
                    <td></td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <table className="ps-std-table ps-std-pay">
        <thead>
          <tr>
            <th>Earnings</th>
            <th className="ps-std-amt">Amount (Rs.)</th>
            {showDeductionColumn ? (
              <>
                <th>Deductions</th>
                <th className="ps-std-amt">Amount (Rs.)</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {payRows.map((row, i) => (
            <tr key={i}>
              <td>{row.earn ? row.earn.component || row.earn.name || row.earn.code : ""}</td>
              <td className="ps-std-amt">{row.earn ? fmtMoney(row.earn.amount) : ""}</td>
              {showDeductionColumn ? (
                <>
                  <td>{row.ded ? row.ded.component || row.ded.name || row.ded.code : ""}</td>
                  <td className="ps-std-amt">{row.ded ? fmtMoney(row.ded.amount) : ""}</td>
                </>
              ) : null}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="ps-std-total">
            <th>Gross Earnings</th>
            <th className="ps-std-amt">{fmtMoney(grossTotal)}</th>
            {showDeductionColumn ? (
              <>
                <th>Total Deductions</th>
                <th className="ps-std-amt">{fmtMoney(totalDeductions)}</th>
              </>
            ) : null}
          </tr>
        </tfoot>
      </table>

      {showFuel ? (
        <table className="ps-std-table ps-std-reimb">
          <tbody>
            <tr>
              <th scope="row">Reimbursements</th>
              <td>Fuel Reimbursement</td>
              <td className="ps-std-amt">{fmtMoney(fuelAmount)}</td>
            </tr>
          </tbody>
        </table>
      ) : null}

      <table className="ps-std-table ps-std-net">
        <tbody>
          <tr>
            <th scope="row">Net salary</th>
            <td className="ps-std-net-amt">{fmtMoney(netSalary)}</td>
          </tr>
          {Number(data.overtime_extra?.hours) > 0 || Number(data.overtime_extra?.amount) > 0 ? (
            <>
              <tr>
                <th scope="row">Overtime hours</th>
                <td className="ps-std-ot-val">{fmtMoney(data.overtime_extra.hours)}</td>
              </tr>
              <tr>
                <th scope="row">Rate per hour</th>
                <td className="ps-std-ot-val">{fmtMoney(otHourlyCharge(data.overtime_extra))}</td>
              </tr>
              <tr>
                <th scope="row">Overtime amount</th>
                <td className="ps-std-net-amt">{fmtMoney(data.overtime_extra.amount)}</td>
              </tr>
              <tr>
                <th scope="row">Amount payable</th>
                <td className="ps-std-net-amt">{fmtMoney(data.amount_payable ?? (netSalary + Number(data.overtime_extra.amount || 0)))}</td>
              </tr>
            </>
          ) : null}
          {hasPayslipFieldValue(data.amount_in_words) && (
            <tr>
              <td colSpan={2} className="ps-std-words">
                <strong>Amount in words:</strong> {data.amount_in_words}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <footer className="ps-std-footer">
        This is a computer-generated payslip and does not require a signature.
      </footer>
    </article>
  );
}
